import React, { useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSocialAccountContentThunk } from 'slices/socialAccounts/thunk';
import { RootState, AppDispatch } from 'store';
import { generateUniqueKey } from 'utils/helpers';
import PostCard from '../PostCard';
import Skeleton from 'react-loading-skeleton';
import SocialPostModal from '../SocialPostModal';
import { closeSocialPostModal, toggleManageCollectionModal } from 'slices/ui/reducer';
import SmartImage from 'Components/Common/SmartImage';
import ContentMasonry from '../ContentMasonry';
import { useElementWidth } from 'hooks/useElementWidth';
import NoDataFound from 'Components/Common/NoDataFound';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import PostPreview from '../PostPreview';
import PostPreviewCreator from '../PostPreviewCreator';
import { SocialPost } from 'types/campaign/collaborations';

const SocialAccountContent: React.FC<{ socialAccountId: string }> = ({ socialAccountId }) => {

    const filters = useSelector((state: RootState) => state.socialAccountState.content.filters);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [filtersId, setFilterId] = useState(generateUniqueKey(filters, ['page']));
    const loading = useSelector((state: RootState) => state.socialAccountState.content.loading);
    const error = useSelector((state: RootState) => state.socialAccountState.content.loading);


    const postsData = useSelector((state: RootState) => state.socialAccountState.content.items[socialAccountId!]?.filtered_lists?.[filtersId!]);
    const posts = postsData?.items ?? [];
    const has_more = postsData?.has_more ?? true;
    const page = postsData?.page ?? 1;
    const wrapperRef = useRef<any>(null);
    const rowWidth = useElementWidth(wrapperRef);


    const [initialLoading, setInitialLoading] = useState(true);


    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        if (socialAccountId && filters) {
            if (filters.page && filters.page === 1) {
                setInitialLoading(true);
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
                dispatch(fetchSocialAccountContentThunk({ socialAccountId, filters })).finally(() => {
                    setInitialLoading(false);
                    setFilterId(generateUniqueKey(filters, ['page']))
                })
            }, 500)
        }
    }, [socialAccountId, filters])

    const nextPage = () => {
        if (has_more && socialAccountId && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchSocialAccountContentThunk({ filters: { ...filters, page: nextPageNumber }, socialAccountId }))
        }
    }

    const bookmark = async (e: any, post: SocialPost) => {
        e.stopPropagation();
        e.preventDefault();
        dispatch(toggleManageCollectionModal({ post, creator: null, itemType: 'post', addMode: true }))
    }

    return (
        <>
            <div className='pb-5'>
                <InfiniteScroll
                    dataLength={posts.length}
                    next={nextPage}
                    className=""
                    hasMore={has_more}
                    loader={<></>}
                >
                    <div ref={wrapperRef}>
                        <ResponsiveMasonry
                            columnsCountBreakPoints={{
                                350: 1,
                                750: 2,
                                900: 3,
                                1200: 4,
                                1500: Math.floor(rowWidth / 230) || 5
                            }}
                            gutterBreakPoints={{
                                350: '24px',
                            }}
                        >
                            <Masonry>
                                {posts.map(p => p.social_post).map((post, i) => (
                                    <div key={i} className='masonry-item' id={`masonry-item-${i}`}>
                                        <PostPreview post={post} onHoverActions={
                                            <span onClick={(e) => bookmark(e, post)} className="position-absolute" style={{ right: '8px', top: '8px', left: 'none' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M17.5 13.4084V9.24728C17.5 5.67344 17.5 3.88652 16.4017 2.77627C15.3033 1.66602 13.5355 1.66602 10 1.66602C6.46447 1.66602 4.6967 1.66602 3.59835 2.77627C2.5 3.88652 2.5 5.67344 2.5 9.24728V13.4084C2.5 15.9889 2.5 17.2791 3.11176 17.8429C3.40351 18.1118 3.77179 18.2807 4.1641 18.3256C4.98668 18.4198 5.94728 17.5701 7.86847 15.8709C8.71768 15.1198 9.14229 14.7442 9.63356 14.6452C9.87548 14.5965 10.1245 14.5965 10.3664 14.6452C10.8577 14.7442 11.2823 15.1198 12.1315 15.8709C14.0527 17.5701 15.0133 18.4198 15.8359 18.3256C16.2282 18.2807 16.5965 18.1118 16.8882 17.8429C17.5 17.2791 17.5 15.9889 17.5 13.4084Z" stroke="white" strokeWidth="1.25" />
                                                    <path d="M12.5 5H7.5" stroke="white" strokeWidth="1.25" strokeLinecap="round" />
                                                </svg>
                                            </span>
                                        } />
                                        {
                                            post.social_account &&
                                            <PostPreviewCreator socialAccount={post.social_account} />
                                        }
                                    </div>
                                ))}
                                {
                                    (loading || initialLoading) &&
                                    Array.from({ length: 10 }).map((_, i) => (
                                        <div key={i} className="explore-item w-100">
                                            <Skeleton width={`100%`} height={i % 2 === 0 ? 420 : 340} />
                                        </div>
                                    ))
                                }
                            </Masonry>
                        </ResponsiveMasonry>
                    </div>
                    {(posts.length === 0 && !initialLoading && !loading) && <div className="my-5"><NoDataFound message='Currently there is no post in your list!' /></div>}
                    </InfiniteScroll>
            </div>
        </>
    )
}

export default SocialAccountContent
